import React, { Suspense } from 'react';
import { Placeholder, VisitorIdentification } from '@sitecore-jss/sitecore-jss-react';
import fastDeepEqual from 'fast-deep-equal/es6/react';
import Helmet from 'react-helmet';
import './assets/styles/globals.scss';
import './assets/styles/styles.scss';
import CustomErrorBoundary from './CustomErrorBoundary';

// Using bootstrap is completely optional. It's used here to provide a clean layout for samples,
// without needing extra CSS in the sample app. Remove it in package.json as well if it's removed here.
// import 'bootstrap/dist/css/bootstrap.css';
// import './assets/app.css';

/*
  APP LAYOUT
  This is where the app's HTML structure and root placeholders should be defined.

  All routes share this root layout by default (this could be customized in RouteHandler),
  but components added to inner placeholders are route-specific.
*/

// inject dictionary props (`t`) into navigation so we can translate it
// NOTE: using this is needed instead of using i18next directly to keep
// the component state updated when i18n state (e.g. current language) changes

const cookieFirstUatScriptSrc =
  'https://consent.cookiefirst.com/sites/cm-web-uat.omodajaecoo.be-72f611b1-d31b-45cb-b294-12fb32c387ce/consent.js';
const cookieFirstProductionScriptSrc =
  'https://consent.cookiefirst.com/sites/omodajaecoo.be-ddf995d8-f32e-4a47-9750-59b4a8801a11/consent.js';
const sitecoreApiHost = process.env.REACT_APP_SITECORE_API_HOST || '';
const cookieFirstScriptSrc =
  process.env.NODE_ENV === 'development' || sitecoreApiHost.includes('uat')
    ? cookieFirstUatScriptSrc
    : cookieFirstProductionScriptSrc;

const Layout = ({ route }) => (
  <React.Fragment>
    {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
    <Helmet>
      <title>
        {(route.fields && route.fields.pageTitle && route.fields.pageTitle.value) || 'Page'}
      </title>

      {/* CookieFirst owns Consent Mode defaults/updates and must load before GTM. */}
      <script id="cookiefirst-script" src={cookieFirstScriptSrc} />

      {/* SEO Meta Tags */}
      {route.fields && route.fields.pageDescription && route.fields.pageDescription.value && (
        <meta name="description" content={route.fields.pageDescription.value} />
      )}
      {route.fields && route.fields.pageKeywords && route.fields.pageKeywords.value && (
        <meta name="keywords" content={route.fields.pageKeywords.value} />
      )}
      {/* Google Tag Manager */}
      <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${route.fields?.globalConfig?.fields?.gtmUid?.value}');`}</script>
      {/* End Google Tag Manager */}
    </Helmet>

    {/* Google Tag Manager (noscript) */}
    <noscript
      dangerouslySetInnerHTML={{
        __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${route.fields?.globalConfig?.fields?.gtmUid?.value}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
      }}
    />
    {/* End Google Tag Manager (noscript) */}
    {/*
      VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
      If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
      For XM (CMS-only) apps, this should be removed.

      VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
    */}
    <VisitorIdentification />
    {/* root placeholder for the app, which we add components to using route data */}
    <Placeholder
      name="jss-header"
      rendering={route}
      componentLoadingMessage={''}
      loadingComponent={() => <></>}
    />
    <main className="main">
      <Placeholder
        name="jss-main"
        rendering={route}
        componentLoadingMessage={''}
        loadingComponent={() => <></>}
      />
    </main>
    <Placeholder
      name="jss-footer"
      rendering={route}
      componentLoadingMessage={''}
      loadingComponent={() => <></>}
    />
  </React.Fragment>
);

// We don't want to re-render `Layout` when route is changed but layout data is not loaded
// Layout will be re-rendered only when layout data is changed
const propsAreEqual = (prevProps, nextProps) => {
  if (fastDeepEqual(prevProps.route, nextProps.route)) return true;

  return false;
};

export default React.memo(Layout, propsAreEqual);
